import { FC, useCallback, useState } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

import { BaseSeparateModal } from 'src/models/modals';
import { UiModal } from 'src/components/UI/modal/UiModal';
import { TemplateGalleryAllScreen } from 'src/components/Modals/TemplateGallery/screens/All';
import { TemplateGalleryMainScreen } from 'src/components/Modals/TemplateGallery/screens/Main';
import { TemplateGalleryDetailsScreen } from 'src/components/Modals/TemplateGallery/screens/Details';
import styles from 'src/styles/common/templateGallery.module.scss';
import { TemplateGalleryHeader } from 'src/components/Modals/TemplateGallery/components/Header';
import {
  showTemplateGalleryModalSelector,
  showTemplateGalleryModalDetailsIdSelector,
} from 'src/store/commonPart/selector';
import {
  hideTemplateGalleryModal,
  cleanTemplateGalleryModalDetailsScreen,
  InitialCommonStateType,
} from 'src/store/commonPart/slice';

type Props = BaseSeparateModal & {
  calledFrom: InitialCommonStateType['templateGalleryModalCalledFrom'];
};

export type ScreenType = 'main' | 'all' | 'details';

type ScreenStateType = {
  current: ScreenType;
  previous: ScreenType | null;
  id?: string;
};

export type SetScreenType = (screen: ScreenType, id?: string) => void;

const TemplateGalleryModalComponent: FC<Props> = ({ onClose, calledFrom }) => {
  const url = useSelector(showTemplateGalleryModalDetailsIdSelector);
  const [screenState, setScreenState] = useState<ScreenStateType>({
    current: url ? 'details' : 'main',
    previous: url ? 'all' : null,
    id: url,
  });
  const dispatch = useDispatch();

  const setScreen: SetScreenType = useCallback(
    (screen, id) => {
      setScreenState((prevState) => ({
        previous: prevState.current,
        current: screen,
        id,
      }));

      dispatch(cleanTemplateGalleryModalDetailsScreen());
    },
    [dispatch]
  );

  const mountAllScreen =
    screenState.current === 'all' || (screenState.current === 'details' && screenState.previous === 'all');

  const [isHidden, setIsHidden] = useState(false);

  return (
    <UiModal
      noFooter
      noBackdropClickClose
      onHide={onClose}
      modalClassName={classNames(styles.modal, styles[`${screenState.current}Screen`], { [styles.isHidden]: isHidden })}
    >
      <TemplateGalleryHeader
        setScreen={setScreen}
        currentScreen={screenState.current}
        prevScreen={screenState.previous}
        onClose={onClose}
        isByTemplateId={!!url}
        calledFrom={calledFrom}
      />

      {mountAllScreen && (
        <TemplateGalleryAllScreen isHidden={screenState.current === 'details'} setScreen={setScreen} />
      )}
      {screenState.current === 'main' && (
        <TemplateGalleryMainScreen setScreen={setScreen} calledFrom={calledFrom} onClose={onClose} />
      )}
      {screenState.current === 'details' && (
        <TemplateGalleryDetailsScreen setScreen={setScreen} id={screenState.id} onHide={setIsHidden} />
      )}
    </UiModal>
  );
};

export const TemplateGalleryModal: FC = () => {
  const { show, calledFrom } = useSelector(showTemplateGalleryModalSelector);

  const dispatch = useDispatch();

  const closeModal = useCallback(() => {
    dispatch(hideTemplateGalleryModal());
  }, [dispatch]);

  return show ? <TemplateGalleryModalComponent onClose={closeModal} calledFrom={calledFrom} /> : null;
};
